import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { BaseButton } from "src/components";
import LoadingSpinner from "src/components/LoadingSpinner";
import RangeDatepickerInline from "src/components/RangeDatepickerInline";
import { useModal } from "src/recoil/modalState/hook";
import { createExcelFile } from "src/utils";
import { getMetaFromExcelFile } from "../../libs";
import useCsData from "../../useCsData";
import QuestionColmns from "../column/QuestionColmns";
import AIReportModal from "./AIreportModal";
import { AIAnalysisData, useAIAnalysis } from "./useAIAnalysis";

const formatStartDate = (date: Date | null) => (date ? moment(date).format("YYYY-MM-DD") + "T00:00:00.000+09:00" : undefined);
const formatEndDate = (date: Date | null) => (date ? moment(date).format("YYYY-MM-DD") + "T23:59:59.999+09:00" : undefined);

const DateSelectModal = () => {
  const { setAbstractModalZ1, setBaseModal, setAbstractModalZ2 } = useModal();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [dataCount, setDataCount] = useState<number | null>(null);
  const abortControllerRef = useRef<AbortController>();
  const isValidDateRange = !!(dateRange[0] && dateRange[1]);
  const { startAnalysis } = useAIAnalysis();
  const [file, setFile] = useState<File | null>(null);
  const { buildAIReportSource, detailedQuestionList, questionListMeta, isFetching } = useCsData({
    noLoading: true,
    questionQueryParams: {
      startDate: formatStartDate(dateRange[0]),
      endDate: formatEndDate(dateRange[1]),
      sort: {
        orders: [{ property: "id", direction: "DESC" }],
      },
    },
    enabled: isValidDateRange,
  });

  /** 파일 업로드 시 접수일시에서 날짜 범위 추출 (개발환경 전용) */
  const handleFileUpload = async (uploadedFile: File) => {
    try {
      const { startDate, endDate, count } = await getMetaFromExcelFile(uploadedFile);
      setFile(uploadedFile);
      setDateRange([startDate, endDate]);
      setDataCount(count);
    } catch (error) {
      console.error("파일 파싱 오류:", error);
      setBaseModal({
        title: "실패",
        isOpen: true,
        children: "엑셀 파일 형식이 잘못되었습니다.",
        btnLeftTitle: "닫기",
        onClose: () => {
          setBaseModal({ isOpen: false });
        },
      });
    }
  };

  /** dateRange가 변경되면 파일 제거 (캘린더에서 직접 선택한 경우) */
  const handleDateRangeChange = (range: [Date | null, Date | null]) => {
    if (file) {
      setFile(null);
      setDataCount(null);
    }
    setDateRange(range);
  };

  const isAnalysisReady = isValidDateRange && (file || (!isFetching && detailedQuestionList && detailedQuestionList.length > 0));

  const { mutate: handleStartAnalysis, isPending: isAnalyzing } = useMutation({
    mutationFn: () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const promise = new Promise<AIAnalysisData[]>(async (resolve, reject) => {
        const startTime = Date.now(); // 시작 시간 기록
        console.log("🔍 Data processing started");

        abortControllerRef.current?.signal.addEventListener("abort", () => {
          reject(new Error("Analysis cancelled"));
          return;
        });

        try {
          let analysisSourceFile = file;

          if (!analysisSourceFile) {
            const csDataList = buildAIReportSource(detailedQuestionList);

            if (!csDataList) {
              throw new Error("undefined excelData");
            }

            const fileName = `Ctrl.room_CS_${moment().format("YYYY-MM-DD HH.mm")}`;
            analysisSourceFile = await createExcelFile({
              data: csDataList,
              fileName,
              columns: QuestionColmns,
            });
          }

          if (dateRange[0] === null || dateRange[1] === null) {
            throw new Error("Analysis cancelled");
          }

          const analysisResult = await startAnalysis([dateRange[0], dateRange[1]], analysisSourceFile, abortControllerRef.current?.signal);

          if (analysisResult.success && analysisResult.data) {
            resolve(analysisResult.data);
          } else {
            reject(analysisResult.error);
          }
        } catch (error) {
          reject(error);
        } finally {
          const endTime = Date.now(); // 종료 시간 기록
          const duration = (endTime - startTime) / 1000; // 초 단위로 변환
          console.log(`⏱️ total processing completed in ${duration} seconds`);
          //센트리 전송

          Sentry.captureMessage(`CS AI total processing completed in ${duration} seconds`, {
            tags: {
              keyword: "CS_AI",
            },
          });
        }
      });

      return promise;
    },
    onSuccess: (AIAnalysisDatas) => {
      console.log("AIAnalysisDatas", AIAnalysisDatas);
      setAbstractModalZ2({
        isOpen: true,
        children: <AIReportModal AIAnalysisDatas={AIAnalysisDatas} dateRange={dateRange} totalElementCount={dataCount ?? 0} />,
        size: "xlarge",
      });
    },
    onError: (error) => {
      if (error.message !== "Analysis cancelled") {
        Sentry.captureException(error, {
          tags: {
            keyword: "CS_AI",
          },
        });

        setBaseModal({
          isOpen: true,
          title: "AI 분석에 실패했습니다",
          children: "불편을 드려 죄송합니다. 잠시 후 다시 시도해 주세요.",
          btnLeftTitle: "닫기",
          btnRightTitle: "기간 선택",
          onClose: () => {
            setBaseModal({ isOpen: false });
            setAbstractModalZ1({ isOpen: false });
            setAbstractModalZ2({ isOpen: false });
          },
          onClick: () => {
            setBaseModal({ isOpen: false });
            setAbstractModalZ2({ isOpen: false });
          },
        });
      }
    },
  });

  const cancelAnalysis = () => {
    if (isAnalyzing && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  /** 분석대상 데이터 개수 */
  useEffect(() => {
    /** 파일이 있을 때는(dev) 파일 기준 카운트가 설정되어 있음 */
    if (file) return;

    const questionListCount = questionListMeta?.pageMeta?.totalElements;
    if (typeof questionListCount === "undefined") return;

    if (dataCount !== questionListCount) setDataCount(questionListCount);
  }, [file, questionListMeta, dataCount]);

  const loadingComponent = (
    <>
      <section className="base-abstract-modal__title text-center">
        <h1>AI 분석 진행 중</h1>
      </section>

      <section className="base-abstract-modal__contents px30">
        <div className="ai-data-analysis-contents-wrap flex-center-center">
          <div className="ai-data-analysis-contents-wrap__result text-center">
            <LoadingSpinner size={86} />
            <p className="mt10">
              고객 문의 내용을 탐험하는 중입니다
              <br />
              거의 다 왔어요
              <br />
              분석 기간 : {moment(dateRange[0]).format("YYYY.MM.DD")} - {moment(dateRange[1]).format("YYYY.MM.DD")}
            </p>
          </div>
        </div>
      </section>

      <section className="base-abstract-modal__btn-wrap justify-center">
        <BaseButton
          title="취소"
          className="color-white"
          onClick={() => {
            cancelAnalysis();
            setAbstractModalZ1({ isOpen: false });
          }}
        />
        <BaseButton
          title="돌아가기"
          className="color-white"
          onClick={() => {
            cancelAnalysis();
          }}
        />
      </section>
    </>
  );

  const dateSelectComponent = (
    <>
      <section className="base-abstract-modal__title text-center">
        <h1>분석기간 선택</h1>
        {process.env.REACT_APP_ENVIRONMENT === "dev" && (
          <div className="px10">
            <h3 className="text-left">개발환경 전용 엑셀파일 직접 업로드</h3>
            <input
              type="file"
              id="dev-excel-file"
              accept=".xlsx,.xls"
              className="d-none"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  handleFileUpload(e.target.files[0]);
                }
              }}
            />
            <label htmlFor="dev-excel-file" className="cursor-pointer">
              {file?.name || "파일 선택"}
            </label>
          </div>
        )}
      </section>

      <section className="base-abstract-modal__contents px30">
        <div className="ai-data-analysis-contents-wrap">
          <RangeDatepickerInline placeholder="캘린더에서 기간을 선택해 주세요" dateRange={dateRange} onChange={handleDateRangeChange} />

          {isFetching && (
            <div className="ai-data-analysis-contents-wrap__loading">
              <LoadingSpinner />
              <p>데이터를 집계중입니다</p>
            </div>
          )}

          {!isFetching &&
            (dataCount !== null && dataCount > 0 ? (
              <div className="ai-data-analysis-contents-wrap__result">
                <div className="text-center mb-2">
                  <h2>집계 완료!</h2>
                  <p>
                    분석 가능한 <span className="font-bold text-blue-500">{dataCount}</span>개의 데이터를 확인했습니다.
                  </p>
                </div>
              </div>
            ) : (
              <div className="ai-data-analysis-contents-wrap__result text-center">
                <p>선택한 기간에 등록된 데이터가 없습니다.</p>
                <p>다시 선택해 주세요.</p>
              </div>
            ))}
        </div>
      </section>

      <section className="base-abstract-modal__btn-wrap justify-center">
        <BaseButton title="취소" className="color-white" onClick={() => setAbstractModalZ1({ isOpen: false })} />
        <BaseButton title="AI 분석 시작" className="color-blue size-medium" disabled={!isAnalysisReady} onClick={() => handleStartAnalysis()} />
      </section>
    </>
  );

  return <>{isAnalyzing ? loadingComponent : dateSelectComponent}</>;
};

export default DateSelectModal;
